import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  exportTableColumnValidation,
  tableColumnValidation,
  TableSettingColumn,
} from 'types/tableSettingsTypes';

interface AdminTransactionState {
  excludedFilter: string[];
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnValidation: tableColumnValidation;
  defaultColumnList: TableSettingColumn[];
  tableColumnList: TableSettingColumn[];
  exportTableColumnValidation: exportTableColumnValidation;
  exportTableColumnList: TableSettingColumn[];
}
const defaultColumnList = [
  {
    name: 'date',
    label_key: 'date',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 1,
  },
  {
    name: 'ffl',
    label_key: 'ffl',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'asyncselect',
    filter_placeholder: 'ffl_all',
    asyncKey: 'ffl',
    option_value_key: 'id',
    option_label_key: 'business_name',
    multi: false,
    order: 2,
  },
  {
    name: 'user',
    label_key: 'user',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 3,
  },
  {
    name: 'type',
    label_key: 'type',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'type_all',
    selectKey: 'transaction_type',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 4,
  },
  {
    name: 'payment_gateway',
    label_key: 'payment_gateway',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'payment_gateway_all',
    selectKey: 'payment_gateway',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 5,
  },
  {
    name: 'payment_type',
    label_key: 'payment_type',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 6,
  },
  {
    name: 'payment_status',
    label_key: 'payment_status',
    allow_list: true,
    allow_filter: true,
    show_list_checkbox: true,
    show_filter_checkbox: true,
    type: 'select',
    filter_placeholder: 'payment_status_all',
    selectKey: 'payment_status',
    option_value_key: 'slug',
    option_label_key: 'name',
    multi: false,
    order: 7,
  },
  {
    name: 'amount',
    label_key: 'amount',
    allow_list: true,
    allow_filter: false,
    show_list_checkbox: true,
    show_filter_checkbox: false,
    order: 8,
  },
];
const defaultConfig = {
  allow_list: true,
  allow_filter: false,
  show_list_checkbox: true,
  show_filter_checkbox: false,
};

const fields = [
  { key: 'date', label: 'date' },
  { key: 'ffl', label: 'ffl' },
  { key: 'user_name', label: 'user_name' },
  { key: 'user_email', label: 'user_email' },
  { key: 'type', label: 'type' },
  { key: 'payment_gateway', label: 'payment_gateway' },
  { key: 'payment_type', label: 'payment_type' },
  { key: 'payment_status', label: 'payment_status' },
  { key: 'amount', label: 'amount' },
];

const exportColumnList = fields.map((field, index) => ({
  name: field.key,
  label_key: field.label,
  order: index + 1,
  ...defaultConfig,
}));
const initialState: AdminTransactionState = {
  excludedFilter: ['search'],
  filterData: {},
  reloadTableData: false,
  tableColumnValidation: {
    minListColumn: 2,
    maxListColumn: 8,
    minFilterColumn: 0,
    maxFilterColumn: 8,
  },
  defaultColumnList: defaultColumnList,
  tableColumnList: JSON.parse(JSON.stringify(defaultColumnList)),
  exportTableColumnValidation: {
    minListColumn: 2,
  },
  exportTableColumnList: exportColumnList,
};

const adminTransactionSlice = createSlice({
  name: 'adminTransaction',
  initialState,
  reducers: {
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      state.filterData = action.payload;
    },
    removeFilterKey(state, action: PayloadAction<string>) {
      delete state.filterData[action.payload];
    },
    resetFilterData(state) {
      state.filterData = {};
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      state.reloadTableData = action.payload;
    },
    setTransactionTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      // settings saved before a column existed don't list it: add it at the end so it shows
      const saved = action.payload;
      const added = defaultColumnList
        .filter((column) => !saved.some((savedColumn) => savedColumn.name === column.name))
        .map((column, index) => ({ ...column, order: saved.length + index + 1 }));
      state.tableColumnList = [...saved, ...added];
    },
  },
});

export const {
  setFilterData,
  removeFilterKey,
  resetFilterData,
  setReloadTableData,
  setTransactionTableColumnList,
} = adminTransactionSlice.actions;

export default adminTransactionSlice.reducer;
